import React, { useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import classNames from "classnames";
import { UserContext } from "../contexts/UserContext";
import Spinner from "../components/Spinner";
import Avatar from "../assets/icons/Avatar.svg";
import CallUser from "../assets/icons/Call.svg";
import Iconfacture from "../components/icons/iconfacture";
import {
  GetUser,
  GetTransfers,
  GetRecharges,
  GetInvoices,
  AddNote,
} from "../helpers/api";
import { toast } from "react-toastify";
import IconTCash from "../components/icons/iconTCash";
import IconTBancaire from "../components/icons/iconTBancaire";
import IconRechargeBancaire from "../components/icons/iconRechargeBancaire";
import Table from "../components/table/table";

export default function UserDetailContainer() {
  const { user } = useContext(UserContext);
  const { id } = useParams();
  const actionSaved = localStorage.getItem("varaction");
  const varAction = actionSaved ? JSON.parse(actionSaved) : {};

  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPage, setTotalPage] = useState(0);

  const [apiResponse, setApiResponse] = useState(null);
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [activeSection, setActiveSection] = useState("");
  const [typeService, setTypeService] = useState("");

  const [loadingData, setLoadingData] = useState(false);
  const [dataresponse, setDataresponse] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const response = await GetUser(
          id,
          user?.accessToken,
          currentPage,
          pageSize
        );
        if (response?.success) {
          setApiResponse(response.data.user);
          setNotes(response.data.notes || []);
        }
      } catch (error) {
        console.error("Fetch Error:", error);
      } finally {
        setIsLoading(false);
      }
    };
    if (user?.accessToken && id) fetchData();
  }, [user?.accessToken, id]);

  useEffect(() => {
    if (!user?.accessToken || !id) return;
    const fetchSectionData = async () => {
      setLoadingData(true);
      try {
        if (activeSection === "transfers") {
          const res = await GetTransfers(
            id,
            user.accessToken,
            currentPage,
            pageSize
          );
          const filterByType = res.data.filter(
            (item) => item.distributionChannel === typeService
          );

          if (res.success) {
            setDataresponse(filterByType);
            setTotalPage(filterByType.length);
          }
        } else if (activeSection === "recharges") {
          const res = await GetRecharges(
            id,
            user.accessToken,
            currentPage,
            pageSize
          );

          if (res.success) {
            setDataresponse(res.data);
            setTotalPage(res.total);
          }
        } else if (activeSection === "factures") {
          const res = await GetInvoices(
            id,
            user.accessToken,
            currentPage,
            pageSize
          );
          if (res.success) {
            setDataresponse(res.data);
            setTotalPage(res.total);
          }
        }
      } finally {
        setLoadingData(false);
      }
    };
    fetchSectionData();
  }, [typeService, id, user?.accessToken, currentPage, pageSize]);

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    try {
      const response = await AddNote(
        {
          userId: id,
          agentId: user?.user?.id,
          commentaire: newNote,
          actionId: varAction?.actionId,
          actionType: varAction?.actionType,
        },
        user?.accessToken
      );
      if (response?.success) {
        toast("Note ajouté avec succée");
        const newlyAddedNote = {
          _id: Date.now(),
          commentaire: response.data?.commentaire,
          date: new Date().toISOString(),
          agent: {
            firstName: user?.user?.firstName,
            lastName: user?.user?.lastName,
          },
        };

        setNotes([newlyAddedNote, ...notes]);
        setNewNote("");
      } else {
        toast("Erreur");
      }
    } catch (error) {
      toast("Erreur");
    }
  };

  if (isLoading)
    return (
      <div className="flex justify-center items-center min-h-screen bg-slate-100 w-full ">
        <Spinner width="w-[60px]" height="h-[60px]" />
      </div>
    );
  if (!apiResponse)
    return (
      <div className="p-8 text-center text-red-500 font-bold">
        Utilisateur non trouvé
      </div>
    );

  const fullPhone = `+${apiResponse.personalInfo?.phone?.prefix}${apiResponse.personalInfo?.phone?.number}`;
  const age = apiResponse.birthDate
    ? new Date().getFullYear() - new Date(apiResponse.birthDate).getFullYear()
    : "—";

  const getStatusStyle = (status) => {
    switch (status?.toLowerCase()) {
      case "canceled":
        return "bg-[#A5A5A5]";
      case "complete":
        return "bg-[#0088FF]";
      case "in progress":
        return "bg-[#FF8D28]";
      default:
        return "bg-gray-100 text-gray-700 border-gray-200";
    }
  };

  const tabService = [
    {
      id: "transfers",
      typeService: "cash_pickup",
      label: "R. Cash",
      icon: IconTCash,
      styleIcon: "stroke-[#5754E5]",
      color: "bg-[#5754E5]",
    },
    {
      id: "transfers",
      typeService: "bank_account",
      label: "C. Compte",
      icon: IconTBancaire,
      styleIcon: "stroke-[#20C374]",
      color: "bg-[#20C374]",
    },
    {
      id: "recharges",
      typeService: "recharges",
      label: "R. Mobile",
      icon: IconRechargeBancaire,
      styleIcon: "stroke-[#FFC000]",
      color: "bg-[#FFC000]",
    },
    {
      id: "factures",
      typeService: "factures",
      label: "P. Facture",
      icon: Iconfacture,
      styleIcon: "stroke-[#38C3FF]",
      color: "bg-[#38C3FF]",
    },
  ];

  const columns = [
    {
      label: "Référence",
      key: "Référence",
      customRenderers: (row) => <div>{row.reference}</div>,
    },
    {
      label:
        activeSection === "transfers"
          ? "Canal"
          : activeSection === "recharges"
          ? "N° Compte"
          : activeSection === "factures"
          ? "Facturier"
          : "",
      key: "column2",
      customRenderers: (row) => (
        <div>
          {activeSection === "transfers"
            ? row.distributionChannel
            : activeSection === "recharges"
            ? row.AccountNumber
            : activeSection === "factures"
            ? row.billerCode
            : ""}
        </div>
      ),
    },
    {
      label: "Status",
      key: "Status",
      customRenderers: (row) => (
        <div className="flex items-center bg-white rounded-[50px] max-w-min py-1 px-3 shadow-[0px_2px_10px_rgba(0,0,0,0.15)] gap-2  text-[10px]">
          <span
            className={`w-[8px] h-[8px] rounded-full ${getStatusStyle(
              row.status
            )}`}
          />
          {row.status}
        </div>
      ),
    },
    {
      label:
        activeSection === "transfers"
          ? "Montant"
          : activeSection === "recharges"
          ? "Valeur"
          : "",
      key: "valeur",
      customRenderers: (row) => (
        <div>
          {activeSection === "transfers"
            ? row.amount?.amountToSend
            : activeSection === "recharges"
            ? row.SendValue
            : ""}
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="min-h-screen w-full font-sans flex flex-col gap-4 ">
        <div className="bg-white border border-[#EEEEEE] w-full p-4 rounded-[10px] flex flex-col items-start gap-3">
          <h2 className="h2-title">Informations Générales</h2>
          <div className="w-full flex items-center justify-between">
            <div className="flex items-center justify-start gap-4 w-1/4 ">
              <img
                className="border-4 rounded-full border-[#20C374]"
                src={Avatar}
                width={50}
                height={50}
                alt="Avatar User"
              />
              <div className="flex flex-col items-start">
                <div className="text-[#A5A5A5] text-[9px] font-semibold">
                  ID
                </div>
                <div className="text-[12px] text[#000]">
                  {apiResponse.order}
                </div>

                <div className="text-[#A5A5A5] text-[9px] font-semibold mt-2">
                  Nom & Prénom
                </div>
                <div className="text-[12px] text[#000]">
                  {apiResponse.personalInfo.lastName}{" "}
                  {apiResponse.personalInfo.firstName}
                </div>
              </div>
            </div>

            <div className="flex flex-col items-start  border-l pl-4 border-[#EEEEEE] w-1/4 ">
              <div className="text-[#A5A5A5] text-[9px] font-semibold">
                EMAIL
              </div>
              <div className="text-[12px] text[#000]">{apiResponse.email}</div>

              <div className="flex w-full  items-center justify-between  mt-2">
                <div className="flex flex-col items-start ">
                  <div className="text-[#A5A5A5] text-[9px] font-semibold ">
                    Téléphone
                  </div>
                  <div className="text-[12px]">{fullPhone}</div>
                </div>
                <a href={`tel:${fullPhone}`}>
                  <img src={CallUser} alt="appel client" />
                </a>
              </div>
            </div>

            <div className="flex flex-col items-start border-l pl-4 border-[#EEEEEE] w-1/4">
              <div className="text-[#A5A5A5] text-[9px] font-semibold">âge</div>
              <div className="text-[12px] text[#000]">{age} ans</div>

              <div className="text-[#A5A5A5] text-[9px] font-semibold mt-2">
                Pays de résidence
              </div>
              <div className="text-[12px] text[#000]">
                {apiResponse.address?.country?.name}
              </div>
            </div>

            <div className="flex flex-col items-start border-l pl-4 border-[#EEEEEE] w-1/4">
              <div className="text-[#A5A5A5] text-[9px] font-semibold">
                Pays de naissance
              </div>
              <div className="text-[12px] text[#000]">
                {apiResponse.countryOfBirth?.name || "—"}
              </div>

              <div className="text-[#A5A5A5] text-[9px] font-semibold mt-2">
                Transactions payées
              </div>
              <div className="text[12px] text-[#5754E5] font-bold flex items-center gap-1">
                {apiResponse.nbTransactionsPaid || 0}
                <span className="text-[6px] text-[#5754E5]">TOTAL</span>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white border border-[#EEEEEE] w-full p-4 rounded-[10px] space-y-7">
          <div className=" flex justify-between items-center">
            <h2 className="h2-title">Actions effectuées par le client</h2>
            <input
              className=" shadow-[0px_2px_10px_rgba(0,0,0,0.1)] flex items-center justify-center placeholder:text-center rounded-[8px] text-[12px] px-4 w-[90px] h-[32px] outline-none"
              placeholder="Filtres"
            />
          </div>

          {varAction?.actionId && varAction?.actionType && (
            <div className="flex flex-col items-start bg-white border border-[#EEEEEE] w-full p-4 rounded-[10px]  gap-4">
              <h2 className="h2-title">Ajouter une note</h2>

              <textarea
                className="w-full p-4   rounded-xl focus:ring-2 focus:ring-[#D1D1FF] focus:border-transparent outline-none transition-all text-sm resize-none"
                rows="3"
                placeholder="Écrire une note sur cet utilisateur..."
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
              ></textarea>

              <button
                onClick={handleAddNote}
                disabled={!newNote.trim()}
                className={classNames(
                  "px-6 py-2 rounded-lg font-bold text-[14px] transition-all ",
                  newNote.trim()
                    ? "bg-[#5d55e8] text-white hover:bg-[#4a44c9] cursor-pointer"
                    : "bg-[#D1D1FF] text-white cursor-not-allowed "
                )}
              >
                Ajouter la note
              </button>
            </div>
          )}

          <div className="flex flex-col items-start bg-white border border-[#EEEEEE] w-full p-4 rounded-[10px]  gap-4 overflow-y-auto scroll max-h-[300px]">
            <h2 className="h2-title ">Notes d’équipe</h2>
            {notes.length > 0 &&
              notes.map((note, index) => (
                <div
                  key={index}
                  className={`${
                    notes.length > index + 1 && "border-b border-[#EEEEEE]"
                  }  w-full pb-4 text-left space-y-4`}
                >
                  <div className="flex items-center gap-4 text-[16px]">
                    <img src={Avatar} width={32} height={32} alt="" />{" "}
                    {user.user.firstName} {user.user.lastName}
                  </div>
                  <p className="text-[#888888] text-[14px]">
                    {note.commentaire}
                  </p>
                  <div className=" flex space-x-[3%] text-[#A5A5A5] text-[10px]">
                    <span className="cursor-pointer">Commenter</span>
                    <span className="cursor-pointer">Modifier</span>
                    <span className="cursor-pointer">Supprimer</span>
                  </div>
                </div>
              ))}
          </div>

          <div className=" w-full flex gap-4">
            {tabService.map((tab, index) => {
              const Icon = tab.icon;
              return (
                <button
                  key={index}
                  onClick={() => {
                    setActiveSection(tab.id);
                    setTypeService(tab.typeService);
                  }}
                  className={classNames(
                    "rounded-[40px] pl-2 pr-3 gap-2 flex items-center h-[32px] shadow-[0px_2px_10px_rgba(0,0,0,0.15)] text-[#000] ",
                    typeService === tab.typeService
                      ? `border-[#5d55e8] text-[#FFF] ${
                          typeService === tab.typeService && tab.color
                        }`
                      : " bg-white text-[#000] hover:bg-gray-50"
                  )}
                >
                  <Icon
                    className={` ${
                      typeService === tab.typeService
                        ? "stroke-[#FFF]"
                        : tab.styleIcon
                    }`}
                  />
                  <span className="text-[12px]">{tab.label}</span>
                </button>
              );
            })}
          </div>

          {loadingData ? (
            <div className="bg-white rounded-2xl shadow-xl p-24 flex flex-col items-center justify-center">
              <Spinner width="w-[50px]" height="h-[50px]" />
            </div>
          ) : (
            <Table
              setPageSize={setPageSize}
              pageSize={pageSize}
              currentPage={currentPage}
              totalPage={totalPage}
              setCurrentPage={setCurrentPage}
              data={dataresponse}
              message="Veuillez Sélectionner une action"
              columns={columns}
              loading={isLoading}
              excel={true}
              rowClassName={(row) => {
                const diff = (new Date(row.createdAt)) / 60000;
                return diff >= 5
                  ? " border-l-4 border-l-[#FF3B30]"
                  : "border-l-4  border-l-[#5754E5]";
              }}
            />
          )}
        </div>
      </div>

      {loadingData && activeSection !== "details" && (
        <div className="bg-white rounded-2xl shadow-xl p-24 flex flex-col items-center justify-center">
          <Spinner width="w-[50px]" height="h-[50px]" />
        </div>
      )}
    </div>
  );
}
